import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MailboxesSection } from './MailboxesSection';
import { MarketingContactsSection } from './MarketingContactsSection';
import { SmsAccountsSection } from './SmsAccountsSection';
import { BrandKitSection } from './BrandKitSection';
import { BookingLinksSection } from './BookingLinksSection';
import { ProfileSection } from './ProfileSection';
import { useRunJtSync, useJtSyncStatus } from '../../api/jt-sync.api';
import { useOrgUsers, useSetEngageRole, type EngageRole, type EffectiveEngageRole, type OrgUser } from '../../api/org-users.api';
import { useAuthStore } from '../../stores/auth.store';
import { useToast } from '../../components/ui/Toast';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { useCan } from '../../hooks/useSession';
import { useStartManageAs } from '../../api/manage-as.api';
import { getApiErrorMessage } from '../../api/client';
import { openImpersonationTab } from '../../lib/impersonation-handoff';
import { useAuditLog, useRetention, useUpdateRetention } from '../../api/compliance.api';
import { useOrgSettings, useUpdateOrgSettings } from '../../api/org-settings.api';
import { initialsFor } from '../../lib/people';
import { SmsOptOutsSection } from './SmsOptOutsSection';
import { SmsWindowCard } from './SmsLimitsCards';
import {
  useSendingDomains,
  useCreateSendingDomain,
  useVerifySendingDomain,
  useConfirmDmarc,
  useUpdateFromAddress,
  useSetPrimarySendingDomain,
  type SendingDomain,
} from '../../api/sending-domains.api';

/* ── Screens 09, 24, 25 — Settings (+ Screen 26 — domain onboarding modal) ── */

type SectionId = 'profile' | 'mailboxes' | 'booking-links' | 'sms' | 'sms-opt-outs' | 'marketing' | 'domains' | 'brand-kit' | 'linki' | 'windows' | 'suppression' | 'permissions' | 'sync' | 'compliance';

// Settings are split into two phases (per the product walkthrough):
//   - 'user'    — an individual's own profile + the connections tied to them.
//                 Every signed-in user sees these.
//   - 'company' — org-wide configuration. Admins only; individual users never
//                 see these entries.
//   - 'team'    — org-wide, but for everyone who can edit campaigns (WRITE and
//                 up): the marketing list (JT1-1925). Shown with the company group.
type SectionScope = 'user' | 'team' | 'company';

/** The section `?section=` names, so other screens can link straight to one (JT1-1925). */
export function sectionFromQuery(search: string): SectionId | null {
  const id = new URLSearchParams(search).get('section');
  return id && SECTIONS.some((s) => s.id === id) ? (id as SectionId) : null;
}

interface Section { id: SectionId; label: string; icon: ReactNode; sub: string; scope: SectionScope }
const SECTIONS: Section[] = [
  { id: 'profile',     label: 'Your profile',     icon: <IconUsers size={14} />,    sub: 'Title + phone for template signatures', scope: 'user' },
  { id: 'mailboxes',   label: 'Mailboxes',        icon: <IconMail size={14} />,     sub: 'Outreach only: never used for newsletters', scope: 'user' },
  { id: 'booking-links', label: 'Booking links',  icon: <IconClock size={14} />,    sub: 'Public calendar URLs for CTA buttons', scope: 'user' },
  { id: 'sms',         label: 'SMS providers',    icon: <IconMail size={14} />,     sub: 'BYO Twilio (+ future providers): SMS channel only', scope: 'company' },
  { id: 'sms-opt-outs', label: 'Text opt-outs',   icon: <IconShield size={14} />,   sub: 'STOP replies and numbers not to text', scope: 'company' },
  { id: 'marketing',   label: 'Marketing list',   icon: <IconMegaphone size={14} />, sub: 'Newsletter opt-ins + suppressions', scope: 'team' },
  { id: 'domains',     label: 'Marketing sending domains', icon: <IconGlobe size={14} />, sub: 'Newsletter From-address: Postmark only', scope: 'company' },
  { id: 'brand-kit',   label: 'Brand kit',        icon: <IconSpark size={14} />,    sub: 'Colours, fonts, logo + footer defaults', scope: 'company' },
  { id: 'linki',       label: 'Linki AI tone',    icon: <IconSpark size={14} />,    sub: 'Voice, length and guardrails', scope: 'company' },
  { id: 'windows',     label: 'Send windows',     icon: <IconClock size={14} />,    sub: 'When candidates can be reached', scope: 'company' },
  { id: 'suppression', label: 'Suppression',      icon: <IconShield size={14} />,   sub: 'Outreach bounces + unsubs', scope: 'company' },
  { id: 'permissions', label: 'Permissions',      icon: <IconUsers size={14} />,    sub: 'Team and role assignments', scope: 'company' },
  { id: 'sync',        label: 'JobTetris sync',   icon: <IconRefresh size={14} />,  sub: 'Records and sync status', scope: 'company' },
  { id: 'compliance',  label: 'Compliance',       icon: <IconLock size={14} />,     sub: 'Retention, audit, residency', scope: 'company' },
];

export function SettingsPage() {
  // Engage ADMINs get both phases; everyone else only their own. UX only —
  // company endpoints enforce ADMIN server-side (see useCan for load-time
  // behaviour).
  const isAdmin = useCan('ADMIN');
  const canWrite = useCan('WRITE');
  // Non-admins see their own sections, plus the team ones when they can edit campaigns.
  const visibleSections = SECTIONS.filter((s) => s.scope === 'user' || (s.scope === 'team' && canWrite) || isAdmin);
  const location = useLocation();
  const [active, setActive] = useState<SectionId>(() => sectionFromQuery(location.search) ?? 'profile');
  const [domainModalOpen, setDomainModalOpen] = useState(false);

  // Guard: if state somehow points at a company section a non-admin can't see
  // (e.g. role changed mid-session), fall back to the profile section.
  const activeAllowed = visibleSections.some((s) => s.id === active);
  const effectiveActive = activeAllowed ? active : 'profile';

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Breadcrumb />
        <HeadingRow />

        <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
          <SettingsNav active={effectiveActive} onSelect={setActive} sections={visibleSections} />

          {effectiveActive === 'profile'       && <ProfileSection />}
          {effectiveActive === 'mailboxes'     && <MailboxesSection />}
          {effectiveActive === 'booking-links' && <BookingLinksSection />}
          {effectiveActive === 'sms'           && <SmsAccountsSection />}
          {effectiveActive === 'sms-opt-outs'  && <SmsOptOutsSection />}
          {effectiveActive === 'marketing'     && <MarketingContactsSection />}
          {effectiveActive === 'domains'       && <DomainsSection onAddDomain={() => setDomainModalOpen(true)} />}
          {effectiveActive === 'brand-kit'     && <BrandKitSection />}
          {effectiveActive === 'linki'         && <LinkiToneSection />}
          {effectiveActive === 'windows'     && <SendWindowsSection />}
          {effectiveActive === 'suppression' && <SuppressionSection />}
          {effectiveActive === 'permissions' && <PermissionsSection />}
          {effectiveActive === 'sync'        && <SyncSection />}
          {effectiveActive === 'compliance'  && <ComplianceSection />}
        </div>
      </div>

      {domainModalOpen && <DomainOnboardingModal onClose={() => setDomainModalOpen(false)} />}
    </>
  );
}

/* ── Header ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li><a href="/dashboard" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>Engage</a></li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><IconChevronRight size={12} /></li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Settings</li>
      </ol>
    </nav>
  );
}

function HeadingRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-4xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>
          Settings
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          Your own profile and connections, plus company-wide configuration for admins.
        </p>
      </div>
      {/* Each section persists independently (mailboxes, marketing, retention,
          domains, etc.), so a global "Save all / Revert" header doesn't have
          anything to bind to. Removed rather than mocked. */}
    </div>
  );
}

/* ── Left nav ── */

function SettingsNav({ active, onSelect, sections }: { active: SectionId; onSelect: (id: SectionId) => void; sections: Section[] }) {
  const userSections = sections.filter((s) => s.scope === 'user');
  // `sections` is already what this person may see, so this holds only the team ones for a non-admin.
  const companySections = sections.filter((s) => s.scope !== 'user');

  const renderItem = (s: Section) => {
    const isActive = s.id === active;
    return (
      <li key={s.id}>
        <button
          type="button"
          onClick={() => onSelect(s.id)}
          aria-pressed={isActive}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            border: 'none',
            borderRadius: 'var(--sidebar-item-radius)',
            backgroundColor: isActive ? 'var(--color-sidebar-active-bg)' : 'transparent',
            color: isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-sidebar-text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'background-color var(--transition-base), color var(--transition-base)',
          }}
          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--color-surface-overlay)'; }}
          onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <span style={{ color: isActive ? 'var(--color-sidebar-active-text)' : 'var(--color-text-muted)', display: 'inline-flex' }}>{s.icon}</span>
          {s.label}
        </button>
      </li>
    );
  };

  return (
    <aside style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <div style={{ padding: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div>
          <NavGroupLabel>Your settings</NavGroupLabel>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {userSections.map(renderItem)}
          </ul>
        </div>
        {/* Company phase: everything for admins; the team sections for anyone who can edit campaigns. */}
        {companySections.length > 0 && (
          <div>
            <NavGroupLabel>Company settings</NavGroupLabel>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {companySections.map(renderItem)}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

function NavGroupLabel({ children }: { children: ReactNode }) {
  return (
    <p style={{ margin: 0, padding: 'var(--space-2) var(--space-3) var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-subtle)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
      {children}
    </p>
  );
}

/* ── Section shell ── */

function SectionShell({
  title, subtitle, rightBadge, action, children,
}: {
  title: string;
  subtitle: string;
  rightBadge?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
              {title}
            </h2>
            {rightBadge}
          </div>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{subtitle}</p>
        </div>
        {action}
      </header>
      <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {children}
      </div>
    </section>
  );
}

/* ── Sending domains ── */

function DomainsSection({ onAddDomain }: { onAddDomain: () => void }) {
  const { data, isLoading } = useSendingDomains();
  const domains = data?.data ?? [];

  return (
    <SectionShell
      title="Marketing sending domains"
      subtitle="Newsletter / bulk-mail domains routed through Postmark. Outreach SMTP mailboxes (under Mailboxes) are strictly separate and never used to send newsletters."
      action={<PrimaryButton onClick={onAddDomain}><IconPlus size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Add domain</span></PrimaryButton>}
    >
      {isLoading && (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading domains…
        </p>
      )}

      {!isLoading && domains.length === 0 && (
        <div style={{ padding: 'var(--space-6)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No sending domains yet. Add one to start sending newsletters.
        </div>
      )}

      {domains.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {domains.map((d) => <DomainRowView key={d.id} domain={d} />)}
        </ul>
      )}
    </SectionShell>
  );
}

function DomainRowView({ domain }: { domain: SendingDomain }) {
  const { addToast } = useToast();
  const verify = useVerifySendingDomain();
  const confirmDmarc = useConfirmDmarc();
  const setPrimary = useSetPrimarySendingDomain();
  const [expanded, setExpanded] = useState(false);

  const canBePrimary = domain.status === 'VERIFIED' && domain.dkim_verified && domain.spf_verified;
  const handleSetPrimary = async () => {
    try {
      await setPrimary.mutateAsync(domain.id);
      addToast({ variant: 'success', title: 'Primary domain updated', description: domain.domain });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not set primary', description: (err as Error).message });
    }
  };

  const handleVerify = async () => {
    try {
      const result = await verify.mutateAsync(domain.id);
      if (result.status === 'VERIFIED') {
        addToast({ variant: 'success', title: 'Domain verified', description: domain.domain });
      } else {
        const missing: string[] = [];
        if (!result.dkim_verified) missing.push('DKIM');
        if (!result.spf_verified) missing.push('SPF');
        addToast({
          variant: 'warning',
          title: 'Not verified yet',
          description: missing.length
            ? `Still waiting on: ${missing.join(', ')}. DNS changes can take up to 24 h.`
            : 'Check the DNS records and try again.',
        });
      }
    } catch {
      addToast({ variant: 'danger', title: 'Verification check failed' });
    }
  };

  const lastChecked = domain.last_verified_check_at
    ? new Date(domain.last_verified_check_at).toLocaleString()
    : 'never';

  return (
    <li style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 'var(--space-4)', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--space-9)', height: 'var(--space-9)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-blue-50)', color: 'var(--color-brand-blue)' }}>
            <IconGlobe size={16} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{domain.domain}</span>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Chip kind={domain.status === 'VERIFIED' ? 'verified' : 'warming'}>
                {domain.status === 'VERIFIED' ? 'Verified' : 'Pending'}
              </Chip>
              {domain.is_primary && <Chip kind="primary">Primary</Chip>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <RecordTick label="DKIM" ok={domain.dkim_verified} />
          <RecordTick label="SPF" ok={domain.spf_verified} />
          <RecordTick label="DMARC" ok={domain.dmarc_verified} />
        </div>

        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Last checked: {lastChecked}
        </span>

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <SecondaryButton small onClick={handleVerify} disabled={verify.isPending}>
            {verify.isPending ? 'Checking…' : 'Check now'}
          </SecondaryButton>
          {!domain.is_primary && canBePrimary && (
            <SecondaryButton small onClick={handleSetPrimary} disabled={setPrimary.isPending}>
              {setPrimary.isPending ? 'Setting…' : 'Make primary'}
            </SecondaryButton>
          )}
          <SecondaryButton small onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide DNS' : 'Show DNS'}
          </SecondaryButton>
        </div>
      </div>

      <FromAddressEditor domain={domain} />

      {domain.last_verified_error && (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
          Last check error: {domain.last_verified_error}
        </div>
      )}

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', padding: 'var(--space-3)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' }}>
          <DnsRecord
            label="DKIM (TXT)"
            ok={domain.dkim_verified}
            host={domain.dkim_host}
            value={domain.dkim_text_value}
            placeholder="Awaiting Postmark. Refresh in a moment."
          />
          {domain.dkim_pending_host && (
            <DnsRecord
              label="DKIM rotation pending (TXT)"
              ok={false}
              host={domain.dkim_pending_host}
              value={domain.dkim_pending_text_value}
              placeholder="Add this alongside the existing DKIM key during rotation."
            />
          )}
          <DnsRecord
            label="Return-Path (CNAME)"
            ok={domain.spf_verified}
            host={`pm-bounces.${domain.domain}`}
            value={domain.return_path_cname}
            placeholder="Postmark Return-Path CNAME: required for SPF alignment."
          />
          <DnsRecord
            label="DMARC (TXT)"
            ok={domain.dmarc_verified}
            host={`_dmarc.${domain.domain}`}
            value="v=DMARC1; p=none; rua=mailto:dmarc-reports@your-domain"
            placeholder="Add this TXT record at _dmarc.<domain> with your reporting address."
          />
          {!domain.dmarc_verified && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Confirm you have published the DMARC TXT record? This is a manual check. Engage does not verify DMARC via DNS.')) return;
                try {
                  await confirmDmarc.mutateAsync(domain.id);
                  addToast({ variant: 'success', title: 'DMARC confirmed' });
                } catch {
                  addToast({ variant: 'danger', title: 'Could not confirm DMARC' });
                }
              }}
              disabled={confirmDmarc.isPending}
              style={{
                alignSelf: 'flex-start',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--btn-radius)',
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-surface-white)',
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              {confirmDmarc.isPending ? 'Confirming…' : 'I have published DMARC'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function FromAddressEditor({ domain }: { domain: SendingDomain }) {
  const { addToast } = useToast();
  const mutation = useUpdateFromAddress();
  const [localPart, setLocalPart] = useState(domain.from_local_part ?? '');
  const [displayName, setDisplayName] = useState(domain.from_name ?? '');

  // Keep local state in sync if the row refreshes (e.g. after another tab edit).
  useEffect(() => { setLocalPart(domain.from_local_part ?? ''); }, [domain.from_local_part]);
  useEffect(() => { setDisplayName(domain.from_name ?? ''); }, [domain.from_name]);

  const dirty = (localPart ?? '') !== (domain.from_local_part ?? '')
    || (displayName ?? '') !== (domain.from_name ?? '');
  const preview = localPart ? `${localPart}@${domain.domain}` : `(local-part)@${domain.domain}`;

  const save = async () => {
    try {
      await mutation.mutateAsync({
        id: domain.id,
        from_local_part: localPart.trim() === '' ? null : localPart.trim(),
        from_name: displayName.trim() === '' ? null : displayName.trim(),
      });
      addToast({ variant: 'success', title: 'From address updated', description: preview });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update From address', description: (err as Error).message });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', padding: 'var(--space-3)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
          Marketing From address
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {displayName ? `${displayName} <${preview}>` : preview}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Local part (before @)</span>
          <input
            type="text"
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value)}
            placeholder="newsletter"
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              backgroundColor: 'var(--color-surface-white)',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Display name (optional)</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Acme Talent Newsletter"
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              backgroundColor: 'var(--color-surface-white)',
            }}
          />
        </label>
      </div>
      {!domain.is_primary && (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Only the primary domain&rsquo;s From address is used at send time. Mark this domain primary once verified.
        </span>
      )}
      <div>
        <SecondaryButton small onClick={save} disabled={!dirty || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save From address'}
        </SecondaryButton>
      </div>
    </div>
  );
}

function DnsRecord({
  label,
  ok,
  host,
  value,
  placeholder,
}: {
  label: string;
  ok: boolean;
  host: string | null | undefined;
  value: string | null | undefined;
  placeholder: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
          {label}
        </span>
        <RecordTick label={ok ? 'verified' : 'pending'} ok={ok} />
      </div>
      {host && (
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>
          Host: {host}
        </code>
      )}
      {value ? (
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)', wordBreak: 'break-all', padding: 'var(--space-1) var(--space-2)', backgroundColor: 'var(--color-surface-white)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)' }}>
          {value}
        </code>
      ) : (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{placeholder}</span>
      )}
    </div>
  );
}

function RecordTick({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: ok ? 'var(--color-success)' : 'var(--color-warning)' }}>
      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: 'var(--radius-full)', backgroundColor: ok ? 'var(--color-success)' : 'var(--color-warning)', color: 'var(--color-text-inverse)' }}>
        {ok ? <IconCheck size={9} /> : <IconAlert size={9} />}
      </span>
      <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'var(--font-weight-semibold)' }}>{label}</span>
    </span>
  );
}

/* ── Linki AI tone ── */

function LinkiToneSection() {
  const [voice, setVoice] = useState('warm');
  const [length, setLength] = useState('concise');
  return (
    <SectionShell
      title="Linki AI tone"
      subtitle="How Linki writes for your team. Applies to drafts, rewrites and suggestions."
      rightBadge={<span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--chip-primary-bg)', border: '1px solid var(--chip-primary-border)', color: 'var(--chip-primary-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>Org-wide</span>}
    >
      <FieldBlock title="Voice" hint="Pick one. Linki applies this to all generations.">
        <ToggleChipRow value={voice} onChange={setVoice} options={[
          { v: 'warm', l: 'Warm and direct' },
          { v: 'formal', l: 'Formal' },
          { v: 'casual', l: 'Casual' },
          { v: 'energetic', l: 'Energetic' },
        ]} />
      </FieldBlock>

      <FieldBlock title="Length" hint="Default body length when Linki drafts from scratch.">
        <ToggleChipRow value={length} onChange={setLength} options={[
          { v: 'short', l: 'Short · under 60 words' },
          { v: 'concise', l: 'Concise · 80-120 words' },
          { v: 'detailed', l: 'Detailed · 150+ words' },
        ]} />
      </FieldBlock>

      <FieldBlock title="Guardrails" hint="">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <ToggleSwitch label="Never auto-send" sub="Linki always shows suggestions first. A person must press Use." defaultOn />
          <ToggleSwitch label="Always disclose recruiter identity" sub="Sender name and JobTetris attribution appear in every signature." defaultOn />
          <ToggleSwitch label="Block embellishment of candidate experience" sub="Linki won&rsquo;t overstate years, titles or impact when drafting." defaultOn />
        </div>
      </FieldBlock>
    </SectionShell>
  );
}

/* ── Send windows ── */

// Day codes stored on OrgSettings.send_days, in display order.
const SEND_DAY_CODES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
const DAY_KEY_TO_CODE: Record<string, string> = { mon: 'MON', tue: 'TUE', wed: 'WED', thu: 'THU', fri: 'FRI', sat: 'SAT', sun: 'SUN' };

function parseHHMM(value: string | null | undefined, fallbackMinutes: number): number {
  if (!value) return fallbackMinutes;
  const [h, m] = value.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return fallbackMinutes;
  return h * 60 + m;
}

function SendWindowsSection() {
  const { data, isLoading } = useOrgSettings();
  const update = useUpdateOrgSettings();
  const { addToast } = useToast();

  const [days, setDays] = useState<Record<string, boolean>>({ mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false });
  const [start, setStart] = useState(9 * 60);
  const [end, setEnd] = useState(17 * 60);
  const [dirty, setDirty] = useState(false);

  // Hydrate from the org settings row once it loads.
  const settings = data?.data;
  useEffect(() => {
    if (!settings) return;
    const enabledCodes = Array.isArray(settings.send_days) ? settings.send_days : [];
    setDays({
      mon: enabledCodes.includes('MON'), tue: enabledCodes.includes('TUE'), wed: enabledCodes.includes('WED'),
      thu: enabledCodes.includes('THU'), fri: enabledCodes.includes('FRI'), sat: enabledCodes.includes('SAT'), sun: enabledCodes.includes('SUN'),
    });
    setStart(parseHHMM(settings.send_window_start, 9 * 60));
    setEnd(parseHHMM(settings.send_window_end, 17 * 60));
    setDirty(false);
  }, [settings?.send_days, settings?.send_window_start, settings?.send_window_end]);

  const setDay = (key: string, value: boolean) => { setDays((p) => ({ ...p, [key]: value })); setDirty(true); };
  const setStartDirty = (v: number) => { setStart(v); setDirty(true); };
  const setEndDirty = (v: number) => { setEnd(v); setDirty(true); };

  const handleSave = async () => {
    const send_days = SEND_DAY_CODES.filter((code) =>
      Object.entries(DAY_KEY_TO_CODE).some(([k, c]) => c === code && days[k]),
    );
    try {
      await update.mutateAsync({ send_days, send_window_start: fmt(start), send_window_end: fmt(end) });
      setDirty(false);
      addToast({ variant: 'success', title: 'Send window saved' });
    } catch {
      addToast({ variant: 'danger', title: 'Could not save send window' });
    }
  };

  return (
    <SectionShell
      title="Send windows"
      subtitle="Org default: campaigns can override these on a per-campaign basis."
      rightBadge={<span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--chip-archived-bg)', border: '1px solid var(--chip-archived-border)', color: 'var(--chip-archived-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>AEST default</span>}
      action={
        <PrimaryButton onClick={handleSave} disabled={!dirty || isLoading || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save send window'}
        </PrimaryButton>
      }
    >
      <FieldBlock title="Sending days" hint="Click to toggle individual days.">
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {[
            { k: 'mon', s: 'M', f: 'Monday' }, { k: 'tue', s: 'T', f: 'Tuesday' }, { k: 'wed', s: 'W', f: 'Wednesday' }, { k: 'thu', s: 'T', f: 'Thursday' }, { k: 'fri', s: 'F', f: 'Friday' }, { k: 'sat', s: 'S', f: 'Saturday' }, { k: 'sun', s: 'S', f: 'Sunday' },
          ].map((d) => {
            const active = days[d.k];
            return (
              <button key={d.k} type="button" aria-pressed={active} aria-label={d.f} onClick={() => setDay(d.k, !days[d.k])} style={{
                width: '44px', height: 'var(--btn-height)', borderRadius: 'var(--radius-input)',
                border: `1px solid ${active ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
                backgroundColor: active ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
                color: active ? 'var(--color-action-primary-text)' : 'var(--color-text-muted)',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
              }}>{d.s}</button>
            );
          })}
        </div>
      </FieldBlock>

      <FieldBlock title="Sending hours" hint="Local to each candidate's timezone where available.">
        <DualSlider start={start} end={end} onStart={setStartDirty} onEnd={setEndDirty} />
      </FieldBlock>

      <ToggleSwitch label="Skip AU public holidays" sub="Inherits the org calendar (Sydney, NSW)." defaultOn />
      <ToggleSwitch label="Throttle per-recipient sends" sub="Limits to 1 email per candidate per 6 business hours." defaultOn />
      <SendFromOwnerDefaultToggle />
      <SmsWindowCard />
    </SectionShell>
  );
}

/** Wired org-wide default for "send from the deal/job owner's mailbox". Trigger
 *  campaigns inherit this unless they set their own override on the campaign
 *  Settings step. */
function SendFromOwnerDefaultToggle() {
  const { data, isLoading } = useOrgSettings();
  const update = useUpdateOrgSettings();
  const on = data?.data?.send_from_owner_default ?? false;
  const pending = isLoading || update.isPending;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', opacity: pending ? 0.7 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>Send from owner mailbox (trigger campaigns)</span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          Default for trigger campaigns: send from the mailbox of the booking link, job or deal owner who started it. Held + admins alerted if the owner has no connected mailbox. Per-campaign override available.
        </span>
      </div>
      <button type="button" role="switch" aria-checked={on} disabled={pending} onClick={() => update.mutate({ send_from_owner_default: !on })} style={{ position: 'relative', width: '36px', height: '20px', borderRadius: 'var(--radius-full)', backgroundColor: on ? 'var(--color-action-primary)' : 'var(--color-border-strong)', border: 'none', cursor: pending ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: '2px', left: on ? '18px' : '2px', width: '16px', height: '16px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-white)', boxShadow: 'var(--shadow-xs)' }} />
      </button>
    </div>
  );
}

/** Generic org-setting boolean toggle, wired to OrgSettings. Used for the
 *  JobTetris write-back switches. */
function OrgSettingToggle({ settingKey, label, sub }: { settingKey: 'jt_activity_push_enabled' | 'jt_forms_contact_sync_enabled' | 'jt_forms_writeback_enabled'; label: string; sub: string }) {
  const { data, isLoading } = useOrgSettings();
  const update = useUpdateOrgSettings();
  const on = Boolean(data?.data?.[settingKey]);
  const pending = isLoading || update.isPending;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', opacity: pending ? 0.7 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{sub}</span>
      </div>
      <button type="button" role="switch" aria-checked={on} disabled={pending} onClick={() => update.mutate({ [settingKey]: !on })} style={{ position: 'relative', width: '36px', height: '20px', borderRadius: 'var(--radius-full)', backgroundColor: on ? 'var(--color-action-primary)' : 'var(--color-border-strong)', border: 'none', cursor: pending ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: '2px', left: on ? '18px' : '2px', width: '16px', height: '16px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-white)', boxShadow: 'var(--shadow-xs)' }} />
      </button>
    </div>
  );
}

const SLIDER_MAX = 24 * 60;
const SLIDER_STEP = 30;
function DualSlider({ start, end, onStart, onEnd }: { start: number; end: number; onStart: (v: number) => void; onEnd: (v: number) => void }) {
  const sp = (start / SLIDER_MAX) * 100;
  const ep = (end / SLIDER_MAX) * 100;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <style>{`
        .jt-set-range { position: absolute; inset: 0; width: 100%; height: 100%; background: transparent; appearance: none; -webkit-appearance: none; pointer-events: none; margin: 0; }
        .jt-set-range::-webkit-slider-thumb { appearance: none; width: 18px; height: 18px; border-radius: var(--radius-full); background: var(--color-surface-white); border: 2px solid var(--color-action-primary); box-shadow: var(--shadow-sm); cursor: grab; pointer-events: auto; margin-top: -7px; }
        .jt-set-range::-moz-range-thumb { width: 18px; height: 18px; border-radius: var(--radius-full); background: var(--color-surface-white); border: 2px solid var(--color-action-primary); cursor: grab; pointer-events: auto; }
        .jt-set-range::-webkit-slider-runnable-track { height: 4px; background: transparent; }
        .jt-set-range:focus { outline: none; }
      `}</style>
      <div style={{ position: 'relative', height: '24px' }}>
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: '4px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)' }} />
        <div aria-hidden="true" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: `${sp}%`, width: `${Math.max(0, ep - sp)}%`, height: '4px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-action-primary)' }} />
        <input type="range" className="jt-set-range" min={0} max={SLIDER_MAX} step={SLIDER_STEP} value={start} aria-label="Earliest send time" onChange={(e) => onStart(Math.min(Number(e.target.value), end - SLIDER_STEP))} />
        <input type="range" className="jt-set-range" min={0} max={SLIDER_MAX} step={SLIDER_STEP} value={end} aria-label="Latest send time" onChange={(e) => onEnd(Math.max(Number(e.target.value), start + SLIDER_STEP))} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Mono>{fmt(start)}</Mono>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>{((end - start) / 60).toFixed(0)}h window</span>
        <Mono>{fmt(end)}</Mono>
      </div>
    </div>
  );
}
function fmt(m: number) { return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; }

/* ── Suppression ── */

function SuppressionSection() {
  return (
    <SectionShell
      title="Suppression"
      subtitle="Org-wide rules. Campaigns inherit these and can add their own exclusions."
      action={<a href="#suppressed" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none' }}>View suppressed (1,284) →</a>}
    >
      <ToggleSwitch label="Auto-suppress on unsubscribe" sub="One unsubscribe blocks every future campaign send for that contact." defaultOn />
      <ToggleSwitch label="Auto-suppress on hard bounce" sub="Remove from active sequence after first 5xx bounce." defaultOn />
      <FieldBlock title="Block competitor domains" hint="">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {['competitor.io', 'rival-recruit.com', 'fastpitch.com.au'].map((d) => (
            <RemovableChip key={d}>{d}</RemovableChip>
          ))}
          <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-input)', border: '1px dashed var(--color-border-strong)', backgroundColor: 'transparent', color: 'var(--color-text-link)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' }}>
            <IconPlus size={10} /> Add domain
          </button>
        </div>
      </FieldBlock>
      <ToggleSwitch label="Respect placement-cooldown" sub="Skip candidates placed in the last 6 months by your org." defaultOn />
    </SectionShell>
  );
}

function RemovableChip({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-1) var(--space-1) var(--space-2)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', color: 'var(--color-text-danger)', fontFamily: 'var(--font-mono)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>
      {children}
      <button type="button" aria-label="Remove" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: 'var(--radius-full)', border: 'none', backgroundColor: 'transparent', color: 'currentColor', cursor: 'pointer' }}>
        <IconX size={10} />
      </button>
    </span>
  );
}

/* ── Permissions / Team (screen 24) ── */

const ROLE_LABELS: Record<EffectiveEngageRole, string> = {
  ADMIN: 'Admin',
  PUBLISH: 'Publish',
  WRITE: 'Write',
  VIEW: 'View',
  UNALLOCATED: 'Unallocated',
};

const ALLOCATABLE_ROLES: { value: EngageRole | 'UNALLOCATED'; label: string }[] = [
  { value: 'ADMIN',       label: 'Admin' },
  { value: 'PUBLISH',     label: 'Publish' },
  { value: 'WRITE',       label: 'Write' },
  { value: 'VIEW',        label: 'View' },
  { value: 'UNALLOCATED', label: 'Unallocated' },
];

const ROLE_LEGEND: { role: EffectiveEngageRole; desc: string }[] = [
  { role: 'ADMIN',       desc: 'Everything' },
  { role: 'PUBLISH',     desc: 'Launch and schedule' },
  { role: 'WRITE',       desc: 'Build and submit' },
  { role: 'VIEW',        desc: 'Read only' },
  { role: 'UNALLOCATED', desc: 'Synced from JT, no access yet' },
];

const PERMISSION_PALETTE = [
  'var(--color-brand-indigo)', 'var(--color-brand-teal)', 'var(--color-brand-blue)',
  'var(--color-brand-navy)', 'var(--color-warning)', 'var(--color-success)',
];
function colorFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return PERMISSION_PALETTE[Math.abs(hash) % PERMISSION_PALETTE.length];
}

function PermissionsSection() {
  const isAdmin = useCan('ADMIN');
  const isImpersonating = useAuthStore((s) => s.impersonationKind !== null);
  // Impersonation sessions resolve to ADMIN server-side, but role changes and
  // starting another Manage as are refused while impersonating — hide them.
  const canChangeRoles = isAdmin && !isImpersonating;
  const { data: users, isLoading } = useOrgUsers();
  const startManageAs = useStartManageAs();
  const { addToast } = useToast();
  const [manageAsTarget, setManageAsTarget] = useState<OrgUser | null>(null);
  const list = users ?? [];
  const manageAsName = manageAsTarget ? manageAsTarget.name || manageAsTarget.email : '';

  const confirmManageAs = async () => {
    const target = manageAsTarget;
    setManageAsTarget(null);
    if (!target) return;
    try {
      const session = await startManageAs.mutateAsync(target.jt_user_id);
      openImpersonationTab({
        token: session.token,
        user: session.target,
        actorEmail: session.actor_email,
        kind: 'manage_as',
        expiresAt: session.expires_at,
      });
    } catch (err) {
      addToast({
        variant: 'danger',
        title: `Could not manage Engage as ${target.name || target.email}`,
        description: getApiErrorMessage(err),
      });
    }
  };
  const allocated = list.filter((u) => (u.effective_engage_role ?? 'UNALLOCATED') !== 'UNALLOCATED').length;
  const unallocated = list.length - allocated;

  return (
    <SectionShell
      title="Team and permissions"
      subtitle="Everyone in your Engage workspace is mirrored from JobTetris. A user must have a JobTetris account before they can use Engage, so there is no separate invite. JT admins automatically receive the Engage Admin role; everyone else syncs in as Unallocated until an admin allocates a role."
      rightBadge={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}>{list.length} synced from JT</span>}
    >
      {/* Roles legend */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)' }}>
        {ROLE_LEGEND.map((r) => (
          <span key={r.role} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <RoleBadge role={r.role} />
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{r.desc}</span>
          </span>
        ))}
      </div>

      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        Who can open Engage is controlled in JobTetris (Settings → Users → Engage). Roles here control what they can do inside Engage.
      </p>

      {isLoading ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Loading team…</p>
      ) : list.length === 0 ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', textAlign: 'center' }}>
          No users synced yet. Run a JobTetris sync to pull your team into Engage.
        </p>
      ) : (
        <div style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
            <thead>
              <tr>
                <Th>Member</Th>
                <Th>Email</Th>
                <Th>JT role</Th>
                <Th>JT access</Th>
                <Th>Engage role</Th>
                <Th align="right" style={{ width: '300px' }}>{' '}</Th>
              </tr>
            </thead>
            <tbody>
              {list.map((u, i) => {
                const effective = (u.effective_engage_role ?? 'UNALLOCATED') as EffectiveEngageRole;
                return (
                  <tr key={u.jt_user_id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                    <Td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <Avatar initials={initialsFor(u.name)} color={colorFor(u.jt_user_id)} />
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{u.name}</span>
                      </span>
                    </Td>
                    <Td><Mono>{u.email}</Mono></Td>
                    <Td><span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{u.role}</span></Td>
                    <Td><JtAccessBadge access={u.engage_access} /></Td>
                    <Td><RoleBadge role={effective} /></Td>
                    <Td align="right">
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                        {u.can_manage_as && !isImpersonating && (
                          <SecondaryButton small onClick={() => setManageAsTarget(u)} disabled={startManageAs.isPending}>
                            Manage as
                          </SecondaryButton>
                        )}
                        <ChangeRoleControl jtUserId={u.jt_user_id} effective={effective} canEdit={canChangeRoles} />
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {allocated} allocated · {unallocated} unallocated
        </span>
        {!canChangeRoles && (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {isImpersonating
              ? 'Role changes aren’t available while managing Engage as someone else.'
              : 'Only admins can change roles.'}
          </span>
        )}
      </div>

      <ConfirmModal
        open={manageAsTarget !== null}
        variant="primary"
        title={`Manage Engage as ${manageAsName}?`}
        description={`You'll open a new tab working as ${manageAsName}. Everything you do is recorded as you, on their behalf. The session ends after 1 hour.`}
        confirmLabel="Open new tab"
        onConfirm={() => { void confirmManageAs(); }}
        onCancel={() => setManageAsTarget(null)}
      />
    </SectionShell>
  );
}

/** JobTetris's per-user Engage access toggle, as last synced. */
function JtAccessBadge({ access }: { access: boolean | null | undefined }) {
  if (access === true) return <Chip kind="verified">On</Chip>;
  if (access === false) return <Chip kind="idle">Off</Chip>;
  return <Chip kind="warming">Unknown</Chip>;
}

/** Inline role allocator. Admins get a dropdown that re-roles the user (or
 *  resets them to unallocated). Everyone else — including admins while
 *  impersonating — sees a dash. */
function ChangeRoleControl({ jtUserId, effective, canEdit }: { jtUserId: string; effective: EffectiveEngageRole; canEdit: boolean }) {
  const setRole = useSetEngageRole();
  const { addToast } = useToast();

  if (!canEdit) {
    return <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>-</span>;
  }

  return (
    <select
      aria-label="Change role"
      value={effective}
      disabled={setRole.isPending}
      onChange={async (e) => {
        const next = e.target.value as EngageRole | 'UNALLOCATED';
        try {
          await setRole.mutateAsync({ jtUserId, role: next === 'UNALLOCATED' ? null : next });
          addToast({ variant: 'success', title: next === 'UNALLOCATED' ? 'Role removed' : `Role set to ${ROLE_LABELS[next]}` });
        } catch (err) {
          addToast({ variant: 'danger', title: 'Could not change role', description: getApiErrorMessage(err) });
        }
      }}
      style={{
        height: 'var(--btn-height-sm)',
        padding: '0 var(--space-2)',
        border: '1px solid var(--input-border)',
        borderRadius: 'var(--input-radius)',
        backgroundColor: 'var(--input-bg)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-primary)',
        cursor: setRole.isPending ? 'wait' : 'pointer',
      }}
    >
      {ALLOCATABLE_ROLES.map((r) => (
        <option key={r.value} value={r.value}>{r.value === effective ? r.label : `Change to ${r.label}`}</option>
      ))}
    </select>
  );
}

function RoleBadge({ role }: { role: EffectiveEngageRole }) {
  const map: Record<EffectiveEngageRole, { bg: string; fg: string; border: string }> = {
    ADMIN:       { bg: 'var(--chip-primary-bg)',     fg: 'var(--chip-primary-text)',     border: 'var(--chip-primary-border)' },
    PUBLISH:     { bg: 'var(--chip-scheduled-bg)',   fg: 'var(--chip-scheduled-text)',   border: 'var(--chip-scheduled-border)' },
    WRITE:       { bg: 'var(--chip-inprogress-bg)',  fg: 'var(--chip-inprogress-text)',  border: 'var(--chip-inprogress-border)' },
    VIEW:        { bg: 'var(--chip-archived-bg)',    fg: 'var(--chip-archived-text)',    border: 'var(--chip-archived-border)' },
    UNALLOCATED: { bg: 'var(--chip-warming-bg)',     fg: 'var(--chip-warming-text)',     border: 'var(--chip-warming-border)' },
  };
  const s = map[role];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: s.bg, border: `1px solid ${s.border}`, color: s.fg, fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)', letterSpacing: 'var(--tracking-wide)' }}>
      {ROLE_LABELS[role]}
    </span>
  );
}

/* ── JobTetris sync (screen 25) ── */

// Live entity rows are built from the JT sync status (record counts) — Engage
// mirrors these records from JobTetris. Descriptions are static copy.
const SYNC_ENTITY_META: { key: keyof JtSyncCounts; name: string; sub: string }[] = [
  { key: 'candidates',   name: 'Candidates',   sub: 'Kept up to date from JobTetris' },
  { key: 'contacts',     name: 'Contacts',     sub: 'CRM contacts in your workspace' },
  { key: 'jobs',         name: 'Jobs',         sub: 'Live and draft job postings' },
  { key: 'pipelines',    name: 'Pipelines',    sub: 'Pipelines across your workspace' },
  { key: 'deals',        name: 'Deals',        sub: 'Open deals from your pipeline' },
  { key: 'applications', name: 'Applications', sub: 'Candidate applications' },
];

type JtSyncCounts = {
  candidates: number; contacts: number; jobs: number; pipelines: number; deals: number; applications: number;
};

function relativeFromNow(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'never';
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleString();
}

function SyncSection() {
  const runSync = useRunJtSync();
  const { data: status, isLoading } = useJtSyncStatus();
  const storedJtToken = useAuthStore((s) => s.jtToken);
  const setJtToken = useAuthStore((s) => s.setJtToken);
  const { addToast } = useToast();

  const lastSyncLabel = relativeFromNow(status?.last_sync_at);
  const everSynced = !!status?.last_sync_at;
  const totalRecords = status
    ? status.candidates + status.contacts + status.jobs + status.pipelines + status.deals + status.applications
    : 0;

  // The session carries the JobTetris credentials captured at login, so a
  // manual sync normally needs no input. We only fall back to a prompt when
  // the session has nothing to authenticate with.
  const handleManualSync = async () => {
    let jtToken = storedJtToken;
    if (!jtToken) {
      const pasted = window.prompt('Re-launch Engage from JobTetris to refresh your session, or paste your JobTetris session credential to sync now:');
      if (!pasted?.trim()) return;
      jtToken = pasted.trim();
      setJtToken(jtToken);
    }

    try {
      const result = await runSync.mutateAsync({ jt_token: jtToken, scope: 'all' });
      const { errors, ...counts } = result as Record<string, number> & { errors?: Record<string, string> };
      const summary = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(', ');
      if (errors && Object.keys(errors).length > 0) {
        addToast({ variant: 'warning', title: 'Sync finished with issues', description: `${summary}. Some records could not be synced. Try again shortly.` });
      } else {
        addToast({ variant: 'success', title: 'Sync complete', description: summary || 'Your records are up to date.' });
      }
    } catch (err) {
      const httpStatus = (err as { response?: { status?: number } })?.response?.status;
      const message = (err as { response?: { data?: { error?: string } }; message?: string })
        ?.response?.data?.error ?? (err as Error).message ?? 'Sync failed';
      addToast({ variant: 'danger', title: 'Sync failed', description: message });
      // A rejected credential means the session expired — clear it so the next
      // attempt re-establishes it, and the watchdog can log out if needed.
      if (httpStatus === 401) setJtToken(null);
    }
  };

  return (
    <SectionShell
      title="JobTetris sync"
      subtitle="Your candidates, contacts and jobs stay up to date with JobTetris automatically. Run a manual sync any time you want the latest straight away."
      rightBadge={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--chip-radius)', backgroundColor: everSynced ? 'var(--chip-success-bg)' : 'var(--chip-warming-bg)', border: `1px solid ${everSynced ? 'var(--chip-success-border)' : 'var(--chip-warming-border)'}`, color: everSynced ? 'var(--chip-success-text)' : 'var(--chip-warming-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>
          <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: 'var(--radius-full)', backgroundColor: everSynced ? 'var(--color-success)' : 'var(--color-warning)' }} />
          {everSynced ? `Last synced ${lastSyncLabel}` : 'Not synced yet'}
        </span>
      }
      action={
        <SecondaryButton onClick={handleManualSync} disabled={runSync.isPending}>
          <IconRefresh size={14} />
          <span style={{ marginLeft: 'var(--space-1)' }}>
            {runSync.isPending ? 'Syncing…' : 'Sync now'}
          </span>
        </SecondaryButton>
      }
    >
      {/* Live summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)' }}>
        <SyncSummaryTile label="Records synced" value={isLoading ? '…' : totalRecords.toLocaleString()} />
        <SyncSummaryTile label="Last sync" value={lastSyncLabel} />
        <SyncSummaryTile label="Sync type" value={status?.last_sync_type ?? '-'} />
      </div>

      {/* Write-back toggles */}
      <FieldBlock title="Write activity back to JobTetris" hint="">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <OrgSettingToggle
            settingKey="jt_activity_push_enabled"
            label="Record activity on JobTetris profiles"
            sub="Mirror every Engage interaction (emails/SMS sent, replies, form completions, meetings) as a note on the candidate/contact's JobTetris timeline."
          />
          <OrgSettingToggle
            settingKey="jt_forms_contact_sync_enabled"
            label="Create contacts + save form PDFs to JobTetris"
            sub="When a new email completes a contact form, create the JobTetris contact and save a PDF of the submission to their Documents. Existing records are never edited."
          />
          <OrgSettingToggle
            settingKey="jt_forms_writeback_enabled"
            label="Update candidate profiles from forms"
            sub="When a candidate changes their details on an Engage form, copy the new values onto their JobTetris profile. The newest answer wins, even if the profile was edited since."
          />
        </div>
      </FieldBlock>

      {/* Live records table */}
      <div style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
          <thead>
            <tr>
              <Th>Records</Th>
              <Th>Last synced</Th>
              <Th align="right">Count</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {SYNC_ENTITY_META.map((e, i) => {
              const count = status ? status[e.key] : 0;
              return (
                <tr key={e.key} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)' }}>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{e.name}</span>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{e.sub}</span>
                    </div>
                  </Td>
                  <Td><span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{lastSyncLabel}</span></Td>
                  <Td align="right"><Mono>{isLoading ? '…' : count.toLocaleString()}</Mono></Td>
                  <Td><Chip kind={everSynced ? 'verified' : 'warming'}>{everSynced ? 'Up to date' : 'Pending first sync'}</Chip></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

function SyncSummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', backgroundColor: 'var(--color-surface-muted)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>{value}</span>
    </div>
  );
}

/* ── Compliance ── */

function ComplianceSection() {
  return (
    <SectionShell title="Compliance" subtitle="Retention window and audit trail.">
      <RetentionForm />
      <AuditLogTable />
    </SectionShell>
  );
}

function RetentionForm() {
  const { addToast } = useToast();
  const { data: current, isLoading } = useRetention();
  const update = useUpdateRetention();
  const [draft, setDraft] = useState<string>(''); // empty string = "no retention"

  // Hydrate the draft when the query lands. Stored as a string so the input
  // can show "" for null without coercing the user into 0.
  const initial = current?.data.message_retention_days;
  useEffect(() => {
    setDraft(initial == null ? '' : String(initial));
  }, [initial]);

  const handleSave = async () => {
    const value = draft.trim() === '' ? null : parseInt(draft, 10);
    if (value !== null && (Number.isNaN(value) || value < 7 || value > 3650)) {
      addToast({ variant: 'warning', title: 'Retention must be 7–3650 days, or blank for forever.' });
      return;
    }
    try {
      await update.mutateAsync({ message_retention_days: value });
      addToast({ variant: 'success', title: value === null ? 'Retention disabled (keep forever)' : `Retention set to ${value} days` });
    } catch {
      addToast({ variant: 'danger', title: 'Could not save retention setting' });
    }
  };

  if (isLoading) {
    return (
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Loading retention…
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-border-default)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
          Message retention
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Engage will delete messages and tracking events older than this every night. Conversations stay; only their content disappears. Leave blank to keep everything forever.
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
          {initial == null
            ? 'Currently keeping messages forever (no retention limit).'
            : `Currently keeping messages for ${initial} ${initial === 1 ? 'day' : 'days'}.`}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="number"
          min={7}
          max={3650}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. 365"
          style={{
            width: '140px',
            height: 'var(--input-height)',
            padding: '0 var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--input-font-size)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            outline: 'none',
          }}
        />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>days</span>
        <button
          type="button"
          onClick={handleSave}
          disabled={update.isPending}
          style={{
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: 'var(--color-action-primary)',
            color: 'var(--color-action-primary-text)',
            border: 'none',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            cursor: update.isPending ? 'not-allowed' : 'pointer',
            opacity: update.isPending ? 0.5 : 1,
          }}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function AuditLogTable() {
  const [filter, setFilter] = useState<string>('');
  const { data, isLoading } = useAuditLog(filter ? { action_prefix: filter } : {});
  const entries = data?.data ?? [];
  // Resolve actor / on-behalf-of ids to member names; fall back to the id.
  const { data: orgUsers } = useOrgUsers();
  const nameById = useMemo(
    () => new Map((orgUsers ?? []).map((u) => [u.jt_user_id, u.name || u.email])),
    [orgUsers],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            Audit log
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Recent org-state-changing actions. Latest 100 shown.
          </span>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter actions"
          style={{
            height: 'var(--input-height)',
            padding: '0 var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--input-font-size)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          <option value="">All actions</option>
          <option value="access.">Access &amp; roles</option>
          <option value="campaign.">Campaigns</option>
          <option value="compliance.">Compliance</option>
          <option value="mailbox_connect.">Mailbox connections</option>
          <option value="manage_as.">Manage as</option>
          <option value="takeover.">Super-admin takeover</option>
          <option value="settings.">Settings</option>
        </select>
      </div>

      {isLoading ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading…
        </p>
      ) : entries.length === 0 ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4)', backgroundColor: 'var(--color-surface-muted)', borderRadius: 'var(--radius-input)', textAlign: 'center' }}>
          No audit log entries yet.
        </p>
      ) : (
        <div style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-muted)', borderBottom: '1px solid var(--color-border-default)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>When</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>Action</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>Actor</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>On behalf of</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                    {new Date(e.occurred_at).toLocaleString()}
                  </td>
                  <td style={{ padding: 'var(--space-2) var(--space-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
                    {e.action}
                  </td>
                  <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                    {e.actor_user_id ? nameById.get(e.actor_user_id) ?? e.actor_user_id.slice(0, 12) + '…' : 'system'}
                    {e.on_behalf_of_user_id
                      ? ` (as ${nameById.get(e.on_behalf_of_user_id) ?? e.on_behalf_of_user_id.slice(0, 12) + '…'})`
                      : ''}
                  </td>
                  <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                    {summariseMetadata(e.metadata)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function summariseMetadata(metadata: Record<string, unknown>): string {
  // Render the most useful keys inline rather than the full JSON dump. Falls
  // back to compact JSON for anything unrecognised so nothing is hidden.
  const keys = Object.keys(metadata);
  if (keys.length === 0) return '-';
  if (metadata.name && typeof metadata.name === 'string') {
    const extra = keys.filter((k) => k !== 'name').slice(0, 2).map((k) => `${k}=${formatVal(metadata[k])}`).join(', ');
    return extra ? `${metadata.name} · ${extra}` : (metadata.name as string);
  }
  return keys.slice(0, 3).map((k) => `${k}=${formatVal(metadata[k])}`).join(', ');
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.length > 30 ? v.slice(0, 30) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v).slice(0, 30);
}

/* ── Field primitives ── */

function FieldBlock({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>{title}</span>
      {children}
      {hint && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>}
    </div>
  );
}

function ToggleChipRow({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {options.map((o) => {
        const active = o.v === value;
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} aria-pressed={active} style={{
            display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)',
            backgroundColor: active ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
            border: `1px solid ${active ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
            color: active ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
          }}>{o.l}</button>
        );
      })}
    </div>
  );
}

function ToggleSwitch({ label, sub, defaultOn = false }: { label: string; sub: string; defaultOn?: boolean }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }} dangerouslySetInnerHTML={{ __html: sub }} />
      </div>
      <button type="button" role="switch" aria-checked={on} onClick={() => setOn((v) => !v)} style={{ position: 'relative', width: '36px', height: '20px', borderRadius: 'var(--radius-full)', backgroundColor: on ? 'var(--color-action-primary)' : 'var(--color-border-strong)', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: '2px', left: on ? '18px' : '2px', width: '16px', height: '16px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-white)', boxShadow: 'var(--shadow-xs)' }} />
      </button>
    </div>
  );
}

function Chip({ kind, children }: { kind: 'verified' | 'warming' | 'primary' | 'idle'; children: ReactNode }) {
  const map = {
    verified: { bg: 'var(--chip-verified-bg)',  fg: 'var(--chip-verified-text)',  border: 'var(--chip-verified-border)' },
    warming:  { bg: 'var(--chip-warming-bg)',   fg: 'var(--chip-warming-text)',   border: 'var(--chip-warming-border)' },
    primary:  { bg: 'var(--chip-primary-bg)',   fg: 'var(--chip-primary-text)',   border: 'var(--chip-primary-border)' },
    idle:     { bg: 'var(--chip-idle-bg)',      fg: 'var(--chip-idle-text)',      border: 'var(--chip-idle-border)' },
  } as const;
  const s = map[kind];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: s.bg, border: `1px solid ${s.border}`, color: s.fg, fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)', letterSpacing: 'var(--tracking-wide)' }}>
      {children}
    </span>
  );
}

function Th({ children, align = 'left', style }: { children: ReactNode; align?: 'left' | 'right'; style?: React.CSSProperties }) {
  return <th style={{ textAlign: align, height: 'var(--table-header-height)', padding: '0 var(--table-cell-padding-x)', backgroundColor: 'var(--table-header-bg)', color: 'var(--table-header-text)', fontSize: 'var(--table-header-size)', fontWeight: 'var(--table-header-weight)', letterSpacing: 'var(--table-header-tracking)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-border-default)', whiteSpace: 'nowrap', ...style }}>{children}</th>;
}
function Td({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)', textAlign: align, verticalAlign: 'middle' }}>{children}</td>;
}
function Mono({ children }: { children: ReactNode }) {
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>{children}</span>;
}
function Avatar({ initials, color, pending = false }: { initials: string; color: string; pending?: boolean }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--avatar-size-md)', height: 'var(--avatar-size-md)', borderRadius: 'var(--avatar-radius)', backgroundColor: pending ? 'var(--color-surface-muted)' : color, border: pending ? '1px dashed var(--color-border-strong)' : 'none', color: pending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)', fontFamily: 'var(--font-sans)', fontSize: 'var(--avatar-font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}>{initials}</span>;
}

function PrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return <button type="button" onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-primary)', color: 'var(--color-action-primary-text)', border: 'none', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', fontFamily: 'var(--font-sans)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>{children}</button>;
}
function SecondaryButton({ children, small, onClick, disabled }: { children: ReactNode; small?: boolean; onClick?: () => void; disabled?: boolean }) {
  return <button type="button" onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', height: small ? 'var(--btn-height-sm)' : 'var(--btn-height)', padding: `0 ${small ? 'var(--btn-padding-x-sm)' : 'var(--btn-padding-x)'}`, borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', color: 'var(--color-action-secondary-text)', border: '1px solid var(--color-action-secondary-border)', fontSize: small ? 'var(--text-xs)' : 'var(--btn-font-size)', fontWeight: 'var(--font-weight-semibold)', fontFamily: 'var(--font-sans)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' }}>{children}</button>;
}

/* ── Screen 26 — Domain onboarding modal ── */

function DomainOnboardingModal({ onClose }: { onClose: () => void }) {
  const { addToast } = useToast();
  const create = useCreateSendingDomain();
  const verify = useVerifySendingDomain();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [domain, setDomain] = useState('send.acme.com');
  const [createdDomain, setCreatedDomain] = useState<SendingDomain | null>(null);

  const handleCreate = async () => {
    if (!domain.trim()) return;
    try {
      const result = await create.mutateAsync(domain.trim());
      setCreatedDomain(result);
      setStep(2);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Could not register domain';
      addToast({ variant: 'danger', title: 'Add domain failed', description: msg });
    }
  };

  const handleVerify = async () => {
    if (!createdDomain) return;
    setVerifyFailed(false);
    try {
      const refreshed = await verify.mutateAsync(createdDomain.id);
      setCreatedDomain(refreshed);
      if (refreshed.status === 'VERIFIED') setStep(4);
      else setVerifyFailed(true);
    } catch {
      setVerifyFailed(true);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="domain-modal-title" style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--modal-overlay-bg)' }}>
      <div style={{ width: 'var(--modal-max-width-lg)', maxWidth: '94vw', backgroundColor: 'var(--color-surface-white)', borderRadius: 'var(--modal-radius)', boxShadow: 'var(--modal-shadow)', display: 'flex', flexDirection: 'column', maxHeight: '88vh', overflow: 'hidden' }}>
        <header style={{ padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-border-default)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <StepIndicator current={step} />
          <button type="button" aria-label="Close" onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: 'var(--radius-input)', border: 'none', backgroundColor: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
            <IconX size={14} />
          </button>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6)' }}>
          {step === 1 && <Step1 domain={domain} onChange={setDomain} />}
          {step === 2 && <Step2 domain={domain} created={createdDomain} />}
          {step === 3 && (verifyFailed ? <Step3Failed /> : <Step3Verifying />)}
          {step === 4 && <Step4 domain={domain} />}
        </div>

        <footer style={{ padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border-default)', display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
          {step === 1 && (
            <>
              <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleCreate} disabled={create.isPending || !domain.trim()}>
                {create.isPending ? 'Registering…' : 'Continue'}
              </PrimaryButton>
            </>
          )}
          {step === 2 && (
            <>
              <SecondaryButton onClick={() => setStep(1)}>Back</SecondaryButton>
              <PrimaryButton onClick={() => { setVerifyFailed(false); setStep(3); handleVerify(); }}>
                I&rsquo;ve added the records
              </PrimaryButton>
            </>
          )}
          {step === 3 && !verifyFailed && (
            <>
              <SecondaryButton onClick={onClose}>Close and notify me</SecondaryButton>
              <PrimaryButton onClick={handleVerify} disabled={verify.isPending}>
                {verify.isPending ? 'Checking…' : 'Run check now'}
              </PrimaryButton>
            </>
          )}
          {step === 3 && verifyFailed && (
            <>
              <SecondaryButton onClick={() => setStep(2)}>Back to DNS records</SecondaryButton>
              <PrimaryButton onClick={handleVerify} disabled={verify.isPending}>
                {verify.isPending ? 'Checking…' : 'Try again'}
              </PrimaryButton>
            </>
          )}
          {step === 4 && (
            <>
              <div />
              <PrimaryButton onClick={onClose}>Done</PrimaryButton>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function StepIndicator({ current }: { current: 1 | 2 | 3 | 4 }) {
  const labels = ['Domain', 'DNS', 'Verify', 'Done'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {[1, 2, 3, 4].map((n) => {
        const active = n === current;
        const done = n < current;
        return (
          <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--step-circle-size)', height: 'var(--step-circle-size)', borderRadius: 'var(--radius-full)', backgroundColor: active ? 'var(--step-circle-active-bg)' : done ? 'var(--step-circle-done-bg)' : 'var(--step-circle-pending-bg)', color: done || active ? 'var(--step-circle-text)' : 'var(--step-circle-pending-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}>
              {done ? <IconCheck size={12} /> : n}
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: active ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)', color: active ? 'var(--color-text-brand)' : 'var(--color-text-muted)' }}>{labels[n - 1]}</span>
            {n < 4 && <span aria-hidden="true" style={{ width: 'var(--space-6)', height: '1px', backgroundColor: 'var(--color-border-default)' }} />}
          </span>
        );
      })}
    </div>
  );
}

function Step1({ domain, onChange }: { domain: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2 id="domain-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
        Add a sending domain
      </h2>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
        Use a dedicated subdomain so your primary domain&rsquo;s reputation isn&rsquo;t affected by outreach volume.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <label htmlFor="dom-input" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Domain</label>
        <input id="dom-input" type="text" value={domain} onChange={(e) => onChange(e.target.value)} placeholder="send.yourbrand.com" style={{ height: 'var(--input-height)', padding: '0 var(--input-padding-x)', border: '1px solid var(--input-border)', borderRadius: 'var(--input-radius)', backgroundColor: 'var(--input-bg)', fontSize: 'var(--input-font-size)', fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)', outline: 'none' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>Recently added in your tenant</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {['outreach.acme.com', 'hire.acme.com', 'careers.acme.com'].map((d) => (
            <button key={d} type="button" onClick={() => onChange(d)} style={{ display: 'inline-flex', alignItems: 'center', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}>{d}</button>
          ))}
        </div>
      </div>
      <div style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
        <strong style={{ color: 'var(--color-info)' }}>Why a subdomain?</strong> Outreach volume on a fresh subdomain warms up independently. If anything goes sideways, your primary domain&rsquo;s deliverability stays clean.
      </div>
    </div>
  );
}

function Step2({ domain, created }: { domain: string; created: SendingDomain | null }) {
  // Prefer the Postmark-issued records when we have them — falls back to
  // placeholder hints if the create() response was missing fields, so the
  // user still sees something useful.
  const records = [
    {
      type: 'DKIM',
      host: created?.dkim_host ?? `pm._domainkey.${domain}`,
      value: created?.dkim_text_value ?? 'Awaiting record from Postmark. Refresh in a moment.',
    },
    {
      type: 'Return-Path (CNAME)',
      host: created ? `pm-bounces.${created.domain}` : `pm-bounces.${domain}`,
      value: created?.return_path_cname ?? 'Awaiting record from Postmark. Refresh in a moment.',
    },
    {
      type: 'DMARC',
      host: `_dmarc.${domain}`,
      value: 'v=DMARC1; p=none; rua=mailto:dmarc-reports@your-domain',
    },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2 id="domain-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
        Add 3 records to your DNS
      </h2>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Add these in your DNS provider. Once saved, click &ldquo;I&rsquo;ve added the records&rdquo; below. Propagation usually takes a few minutes.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {records.map((r) => (
          <li key={r.type} style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', display: 'grid', gridTemplateColumns: '80px 1fr auto auto', gap: 'var(--space-3)', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-blue-50)', border: '1px solid var(--color-blue-100)', color: 'var(--color-brand-blue)', fontFamily: 'var(--font-mono)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)' }}>
              {r.type}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Host · {r.host}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.value}</span>
            </div>
            <span className="chip chip-pending">Pending</span>
            <SecondaryButton small><IconCopy size={12} /><span style={{ marginLeft: 'var(--space-1)' }}>Copy</span></SecondaryButton>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step3Verifying() {
  const items = [
    { label: 'DKIM',  state: 'ok' as const, msg: 'Found' },
    { label: 'SPF',   state: 'ok' as const, msg: 'Found' },
    { label: 'DMARC', state: 'wait' as const, msg: 'Not seen yet' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <style>{`@keyframes jt-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span style={{ display: 'inline-block', width: '24px', height: '24px', borderRadius: 'var(--radius-full)', border: '3px solid var(--color-border-default)', borderTopColor: 'var(--color-brand-blue)', animation: 'jt-spin 0.9s linear infinite' }} />
        <h2 id="domain-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
          Checking your DNS records…
        </h2>
      </div>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Attempt 2 of 6 · most providers propagate within 5–15 minutes, some up to an hour.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {items.map((it) => (
          <li key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: 'var(--radius-full)', backgroundColor: it.state === 'ok' ? 'var(--color-success)' : 'var(--color-warning)', color: 'var(--color-text-inverse)' }}>
              {it.state === 'ok' ? <IconCheck size={10} /> : <IconClock size={10} />}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{it.label}</span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: it.state === 'ok' ? 'var(--color-success)' : 'var(--color-text-warning)' }}>{it.msg}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step3Failed() {
  const items = [
    { label: 'DKIM',  msg: 'Found but signature mismatch · regenerate key' },
    { label: 'SPF',   msg: 'Missing include: postmark-mail.com' },
    { label: 'DMARC', msg: 'Record not found · check the host value (_dmarc)' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
          <IconX size={16} />
        </span>
        <h2 id="domain-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
          We couldn&rsquo;t find your records.
        </h2>
      </div>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Three records didn&rsquo;t verify. Double-check the values and host names, then try again.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {items.map((it) => (
          <li key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)' }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-danger)', color: 'var(--color-text-inverse)' }}>
              <IconX size={10} />
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-danger)' }}>{it.label}</span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-danger)' }}>{it.msg}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step4({ domain }: { domain: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-6) 0', textAlign: 'center' }}>
      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
        <IconCheck size={24} />
      </span>
      <h2 id="domain-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
        You&rsquo;re ready to send.
      </h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{domain}</span>
        <Chip kind="primary">Primary</Chip>
        <Chip kind="verified">Verified</Chip>
      </div>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: '420px' }}>
        DKIM, SPF and DMARC are all aligned. Engage will start a 7-day warm-up before allowing full daily volume.
      </p>
    </div>
  );
}

/* ── Icons ── */

function IconChevronRight({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconPlus({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function IconX({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function IconCheck({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>; }
function IconAlert({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>; }
function IconGlobe({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>; }
function IconMail({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>; }
function IconMegaphone({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-8v18l-18-8z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></svg>; }
function IconClock({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }
function IconShield({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>; }
function IconUsers({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
function IconRefresh({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>; }
function IconLock({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>; }
function IconSpark({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.96L21 11l-6.61 2.04L12 20l-2.39-6.96L3 11l6.61-2.04L12 2z" /></svg>; }
function IconCopy({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>; }
